import { useRef, useState } from 'react';

import {
  BREAKPOINTS,
  useMediaQuery,
} from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import BpkMap, { withGoogleMapsScript } from '@skyscanner-internal/backpack-web/bpk-component-map';
import { getInitialBounds } from '@web-platform/travel-tools-flight-price-map-utils-frontend';

import { FlightCard } from '../FlightCard/FlightCard';

import { MapMarker } from './Markers/MapMarker';
import { usePanToSelected } from './usePanToSelected';

import type { GoogleMapInstance } from './usePanToSelected';
import type { FlightCardProps } from '../FlightCard/FlightCard';
import type { Bounds } from '@web-platform/travel-tools-flight-price-map-utils-frontend';

import STYLES from './ConfidenceMap.module.scss';

export type { Bounds };

export type MarkerProps = FlightCardProps & {
  coordinates: { latitude: number; longitude: number };
};

export type ConfidenceMapProps = {
  googleMapsApiKey: string;
  markers?: MarkerProps[];
  selectedMarkerId?: string;
  mapId?: string;
  showControls?: boolean;
  initialBounds?: Bounds;
  onMarkerClick?: (id: string) => void;
};

const INITIAL_BOUNDS_DEFAULT = { north: 58, south: 44, west: -8, east: 18 };

const BpkMapWithScript = withGoogleMapsScript(BpkMap);

export const ConfidenceMap = ({
  googleMapsApiKey,
  initialBounds,
  mapId,
  markers = [],
  onMarkerClick,
  selectedMarkerId,
  showControls = true,
}: ConfidenceMapProps) => {
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const bounds = initialBounds ?? getInitialBounds(markers, INITIAL_BOUNDS_DEFAULT, isMobile);
  const mapInstanceRef = useRef<GoogleMapInstance | null>(null);

  usePanToSelected({ mapInstance: mapInstanceRef.current, markers, selectedMarkerId });

  const handleMapRef = (map: GoogleMapInstance) => {
    mapInstanceRef.current = map;
  };

  const handleMarkerClick = (id: string) => {
    if (!isMobile) {
      setIsPopoverOpen((prev) => (selectedMarkerId === id ? !prev : true));
    }
    onMarkerClick?.(id);
  };

  const selectedMarker = selectedMarkerId
    ? markers.find((m) => m.destinationAirportCode === selectedMarkerId)
    : undefined;

  const selectedMarkerCardProps = selectedMarker
    ? (({ coordinates: _coordinates, ...rest }) => rest)(selectedMarker)
    : undefined;

  return (
    <div className={STYLES.mapContainer}>
      <BpkMapWithScript
        googleMapsApiKey={googleMapsApiKey}
        mapId={mapId}
        className={STYLES.map}
        bounds={bounds}
        showControls={showControls}
        greedyGestureHandling
        mapRef={handleMapRef}
      >
        {markers.map((marker) => (
          <MapMarker
            key={marker.destinationAirportCode}
            marker={marker}
            selectedMarkerId={selectedMarkerId}
            onMarkerClick={handleMarkerClick}
            isOpen={isPopoverOpen}
            onClose={() => setIsPopoverOpen(false)}
          />
        ))}
      </BpkMapWithScript>
      {isMobile && selectedMarkerCardProps && (
        <div className={STYLES.floatingCard} data-testid="floating-flight-card">
          <FlightCard {...selectedMarkerCardProps} area="map" />
        </div>
      )}
    </div>
  );
};
